import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';

const WorkspaceContext = createContext();

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

export const WorkspaceProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);

  // Demo workspace data
  const demoWorkspace = {
    id: 'demo',
    name: 'Stripe',
    tagline: 'The new standard in online payments',
    logo: 'S',
    website: 'https://stripe.com',
    founded: '2010-01-01',
    location: 'San Francisco, CA',
    stage: 'growth',
    isDemo: true
  };

  // Fetch workspaces (companies) for the current user
  useEffect(() => {
    const fetchWorkspaces = async () => {
      if (!currentUser) {
        setWorkspaces([demoWorkspace]);
        setSelectedWorkspace(demoWorkspace);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const companiesRef = collection(db, 'companies');
        const q = query(companiesRef, where('userId', '==', currentUser.uid));
        const querySnapshot = await getDocs(q);
        
        const companiesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Add demo workspace to the list
        const allWorkspaces = [demoWorkspace, ...companiesData];
        setWorkspaces(allWorkspaces);

        // Load saved workspace selection or default to first workspace
        const savedWorkspaceId = localStorage.getItem('selectedWorkspaceId');
        
        if (savedWorkspaceId) {
          const savedWorkspace = allWorkspaces.find(w => w.id === savedWorkspaceId);
          if (savedWorkspace) {
            setSelectedWorkspace(savedWorkspace);
          } else {
            // If saved workspace doesn't exist, select first available
            setSelectedWorkspace(allWorkspaces[0]);
            localStorage.setItem('selectedWorkspaceId', allWorkspaces[0].id);
          }
        } else {
          // Auto-select first workspace if no saved selection
          if (allWorkspaces.length > 0) {
            setSelectedWorkspace(allWorkspaces[0]);
            localStorage.setItem('selectedWorkspaceId', allWorkspaces[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching workspaces:', error);
        // Fallback to demo workspace
        setWorkspaces([demoWorkspace]);
        setSelectedWorkspace(demoWorkspace);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkspaces();
  }, [currentUser]);

  // Save workspace selection to localStorage
  const selectWorkspace = (workspace) => {
    setSelectedWorkspace(workspace);
    localStorage.setItem('selectedWorkspaceId', workspace.id);
  };

  // Refresh workspaces list
  const refreshWorkspaces = async () => {
    if (!currentUser) return;

    try {
      const companiesRef = collection(db, 'companies');
      const q = query(companiesRef, where('userId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      const companiesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const allWorkspaces = [demoWorkspace, ...companiesData];
      setWorkspaces(allWorkspaces);

      // Keep selected workspace in sync with fresh data (e.g. after name change)
      const currentWorkspace = allWorkspaces.find(w => w.id === selectedWorkspace?.id);
      if (currentWorkspace) {
        setSelectedWorkspace(currentWorkspace);
      } else if (allWorkspaces.length > 0) {
        setSelectedWorkspace(allWorkspaces[0]);
        localStorage.setItem('selectedWorkspaceId', allWorkspaces[0].id);
      }
    } catch (error) {
      console.error('Error refreshing workspaces:', error);
    }
  };

  const value = {
    selectedWorkspace,
    workspaces,
    loading,
    selectWorkspace,
    refreshWorkspaces
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};
